import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { FaSpinner, FaClock, FaExclamationCircle, FaInfoCircle } from 'react-icons/fa';

// The exact sequence of full step names, in order:
const stepOrder = [
  'Protocol consent',                  // index = 0
  'Protocol patient-preparation',      // index = 1
  'Protocol patient-positioning',      // index = 2
  'Protocol positioning',              // index = 3
  'Protocol image-acquisition',        // index = 4
  'Protocol image-analysis',           // index = 5
  'Protocol image-reporting',          // index = 6
  'Protocol post-counselling',         // index = 7
  'Protocol quiz',                     // index = 8
  'Protocol quiz-result'               // index = 9
];

// Helper to map protocolId (number or string) → index in stepOrder
function getStepIndex(protocolId) {
  // If protocolId is a number in [1..length], use that
  const maybeNum = Number(protocolId);
  if (!isNaN(maybeNum) && maybeNum >= 1 && maybeNum <= stepOrder.length) {
    return maybeNum - 1;
  }

  // Otherwise treat as string: match the end of each stepOrder entry
  const key = protocolId.toString().trim().toLowerCase();
  for (let i = 0; i < stepOrder.length; i++) {
    if (stepOrder[i].toLowerCase().endsWith(key)) {
      return i;
    }
  }

  // If no match, put at end
  return stepOrder.length;
}

export default function ProtocolHistory({ registrationId }) {
  const apiUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5001').trim();

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  useEffect(() => {
    if (!registrationId) {
      setError('No patient selected.');
      return;
    }
    setLoading(true);
    setError(null);

    axios
      .get(`${apiUrl}/api/protocol-times`, {
        params: { registration_id: registrationId }
      })
      .then(res => setRecords(res.data))
      .catch(err => {
        console.error(err);
        setError('Failed to load protocol history');
      })
      .finally(() => setLoading(false));
  }, [apiUrl, registrationId]);

  // Sort records in the exact stepOrder sequence
  const sortedRecords = useMemo(() => {
    return [...records].sort((a, b) => {
      const ai = getStepIndex(a.protocolId);
      const bi = getStepIndex(b.protocolId);
      return ai - bi;
    });
  }, [records]);

  // Compute total duration across all records
  const totalSeconds = useMemo(() => {
    return records.reduce((sum, r) => sum + (r.durationSeconds || 0), 0);
  }, [records]);

  // Formatting helpers
  const formatTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour:   '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year:  'numeric',
      month: 'short',
      day:   'numeric'
    });
  };

  const formatDuration = (seconds) => {
    if (seconds >= 3600) {
      const hrs = Math.floor(seconds / 3600);
      const rem = seconds % 3600;
      const mins = Math.floor(rem / 60);
      const secs = rem % 60;
      return `${hrs}h ${mins}m ${secs}s`;
    }
    if (seconds >= 60) {
      const mins = Math.floor(seconds / 60);
      const remSec = seconds % 60;
      return `${mins}m ${remSec}s`;
    }
    return `${seconds}s`;
  };

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6">
        <h2 className="text-2xl font-bold text-white">Protocol History</h2>
      </div>

      <div className="p-6 max-h-[70vh] overflow-y-auto">
        {!registrationId ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
            <FaExclamationCircle className="text-red-500 text-2xl mx-auto mb-2" />
            <p className="text-red-600 font-medium">No patient selected</p>
          </div>
        ) : loading ? (
          <div className="flex justify-center items-center py-12">
            <FaSpinner className="animate-spin text-4xl text-blue-600" />
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
            <FaExclamationCircle className="text-red-500 text-2xl mx-auto mb-2" />
            <p className="text-red-600 font-medium">{error}</p>
          </div>
        ) : records.length === 0 ? (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
            <FaInfoCircle className="text-gray-400 text-4xl mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-600">No Protocol History Found</h3>
            <p className="text-gray-500 mt-2">
              There are no protocol records available for this patient.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Total duration displayed at the top */}
            <div className="mb-4">
              <p className="text-gray-700 font-semibold">
                Total Time:&nbsp;
                <span className="text-indigo-600">{formatDuration(totalSeconds)}</span>
              </p>
            </div>

            {sortedRecords.map((record, index) => {
              // Derive displayName from stepOrder based on protocolId
              const stepIdx = getStepIndex(record.protocolId);
              const displayName =
                stepIdx >= 0 && stepIdx < stepOrder.length
                  ? stepOrder[stepIdx]
                  : `Protocol ID ${record.protocolId || 'N/A'}`;

              return (
                <div
                  key={index}
                  className={`relative pl-8 pb-6 ${
                    index !== sortedRecords.length - 1 ? 'border-l-2 border-blue-200' : ''
                  }`}
                >
                  <div className="absolute left-0 top-0 w-4 h-4 bg-blue-500 rounded-full -translate-x-1/2" />
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-800">
                          {displayName}
                        </h3>
                        <p className="text-sm text-gray-500">
                          Duration: {formatDuration(record.durationSeconds)}
                        </p>
                      </div>
                      <div className="bg-blue-50 text-blue-800 px-3 py-1 rounded-full text-sm font-medium inline-flex items-center">
                        <FaClock className="text-blue-500 mr-2" />
                        <span>Completed</span>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-sm text-gray-500">Start Time</p>
                        <p className="font-medium text-gray-800">{formatTime(record.startTime)}</p>
                        <p className="text-xs text-gray-400">{formatDate(record.startTime)}</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-sm text-gray-500">End Time</p>
                        <p className="font-medium text-gray-800">{formatTime(record.endTime)}</p>
                        <p className="text-xs text-gray-400">{formatDate(record.endTime)}</p>
                      </div>
                    </div>
                    <div className="mt-4 bg-gray-50 rounded-lg p-3">
                      <p className="text-sm text-gray-500">Logged At</p>
                      <p className="font-medium text-gray-800">{formatTime(record.loggedAt)}</p>
                      <p className="text-xs text-gray-400">{formatDate(record.loggedAt)}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
